import {
	SalesChannelCategoryGroupProductsDocument,
	type SalesChannelCategoryGroupProductsQuery,
	type SalesChannelCategoryGroupProductsQueryVariables,
} from "@/generated/graphql";
import { useCallback } from "react";
import { useClient } from "urql";

const PAGE_SIZE = 500;

// Product picker limited to the menu group's catalog.
export const useMenuGroupProductAutocomplete = (
	salesChannelCategoryGroupID: string,
) => {
	const client = useClient();

	const load = useCallback(
		async (search: string) => {
			if (!salesChannelCategoryGroupID) {
				return { items: [], total: 0, hasMore: false };
			}

			const { data, error } = await client
				.query<
					SalesChannelCategoryGroupProductsQuery,
					SalesChannelCategoryGroupProductsQueryVariables
				>(
					SalesChannelCategoryGroupProductsDocument,
					{
						qop: {
							pagination: { page: 1, pageSize: PAGE_SIZE, withCount: false },
							sorts: [{ field: "display_order", direction: "asc" }],
							filters: { salesChannelCategoryGroupID },
						},
					},
					{ requestPolicy: "network-only" },
				)
				.toPromise();

			if (error) throw error;

			const keyword = search.trim().toLowerCase();
			const matched = (
				data?.salesChannelCategoryGroupProducts.items ?? []
			).filter((item) => {
				if (!keyword) return true;
				const productName = item.product?.productName ?? item.productName;
				const productCode = item.product?.productCode ?? item.productCode;
				return `${productName} ${productCode} ${item.barcode ?? ""}`
					.toLowerCase()
					.includes(keyword);
			});

			return {
				items: matched.map((item) => {
					const productName = item.product?.productName ?? item.productName;
					const productCode = item.product?.productCode ?? item.productCode;
					const identifier = item.barcode || productCode;
					return {
						value: item.productID,
						label: identifier ? `${productName} (${identifier})` : productName,
						raw: {
							productID: item.productID,
							productName,
							productCode,
							barcode: item.barcode || item.product?.barcode || "",
						},
					};
				}),
				total: matched.length,
				hasMore: false,
			};
		},
		[client, salesChannelCategoryGroupID],
	);

	return { load };
};
